/**
 * Support-report draft shown in the CEPI chat. Nothing is sent until the user
 * clicks Submit; Cancel discards the draft without any request.
 */
import React, { useState } from 'react';
import { submitSupportReport } from '../../services/supportReport';

export interface SupportReportCardProps {
  /** Last CEPI question. */
  query: string;
  /** The page's own search query (Keyword QUERY line); empty when the page has none. */
  searchQuery: string;
  /** Readable page name shown in the card; `page` (title + path) is only emailed. */
  pageLabel: string;
  page: string;
  conversationId: string | null;
  onCancel: () => void;
}

export const SupportReportCard: React.FC<SupportReportCardProps> = ({
  query, searchQuery, pageLabel, page, conversationId, onCancel,
}) => {
  const [notes, setNotes] = useState('');
  const [removed, setRemoved] = useState({ search: false, query: false });
  const [status, setStatus] = useState<'draft' | 'sending' | 'sent'>('draft');
  const [error, setError] = useState<string | null>(null);

  if (status === 'sent') {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="support-report-sent">
        Sent to support. We will get back to you by email.
      </div>
    );
  }

  const submit = async () => {
    setStatus('sending');
    setError(null);
    try {
      await submitSupportReport({
        query: removed.query ? '' : query,
        search_query: removed.search ? '' : searchQuery,
        page,
        notes, conversation_id: conversationId });
      setStatus('sent');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your report could not be sent.');
      setStatus('draft');
    }
  };

  return (
    <div className="min-w-0 max-w-full shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700" data-testid="support-report-card">
      <div className="mb-1 font-semibold text-ink">Send this to Precepi support?</div>
      {([
        ['search', 'Search', searchQuery, 'support-report-search'],
        ['query', 'Last CEPI question', query, 'support-report-query'],
      ] as const).map(([k, label, value, testId]) => value && !removed[k] && (
        <div key={k} className="mb-1">
          <div className="flex items-center justify-between text-slate-500">
            <span>{label}</span>
            <button
              type="button"
              onClick={() => setRemoved((r) => ({ ...r, [k]: true }))}
              aria-label={`Remove ${label}`}
              className="underline hover:text-slate-700"
            >
              Remove
            </button>
          </div>
          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]" data-testid={testId}>{value}</div>
        </div>
      ))}
      <div className="text-slate-500">Page</div>
      <div className="mb-1 break-words [overflow-wrap:anywhere]" data-testid="support-report-page">{pageLabel || '(unknown)'}</div>
      <label className="text-slate-500" htmlFor="support-report-notes">Notes</label>
      <textarea
        id="support-report-notes"
        data-testid="support-report-notes"
        value={notes}
        maxLength={5000}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="What went wrong? (optional)"
        rows={3}
        className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1 text-xs"
      />
      {error && <div className="mt-1 text-red-700" role="alert" data-testid="support-report-error">{error}</div>}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={status === 'sending'}
          className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={status === 'sending'}
          className="rounded bg-ink px-2 py-1 font-medium text-white disabled:opacity-60"
        >
          {status === 'sending' ? 'Sending…' : 'Submit'}
        </button>
      </div>
    </div>
  );
};
